// @flow
import * as turf from '@turf/turf';
import _ from 'lodash';
import numeral from 'numeral';

import templates from '../templates';
import { FeaturePeriod } from './types/feature-period.type';
import environment from '../../environment';
import moment from "moment";

declare var $: any;
declare var kendo: any;
const LayerControls = (name) => require('../../controls').get(name);
let _visible;

class Dialog {
    content: $;
    container: $;
    chartType: string = 'pie';
    _layerPeriods: FeaturePeriod[];
    _name: string;
    _typeName: string;
    rangePicking: boolean;
    refreshing: boolean;
    isAfterSales: boolean = environment.get('CONFIG.IS_AFTER_SALES') === true;

    seriesColors = [
        '#2196f3',
        '#00bcd4',
        '#4caf50',
        '#cddc39',
        '#ffc107',
        '#ff5722',
        '#e91e63',
        '#9c27b0',
        '#3f51b5',
        '#03a9f4',
        '#009688',
        '#8bc34a',
        '#ffeb3b',
        '#ff9800',
        '#f44336',
    ];

    constructor(name: string, area: any, contexts?: any[], typeName?: string, isShowDialog = true) {
        if (!isShowDialog) return;

        (this: any).updateDataSources = this.updateDataSources.bind(this);

        this.isAfterSales = environment.get('CONFIG.IS_AFTER_SALES') === true;

        if (!this.isAfterSales) {
            this._setPeriod(contexts);
        }

        this._name = name;
        this._typeName = typeName;

        const periodLabel = !this.isAfterSales && this._layerPeriods
            ? this._layerPeriods.map(({ label }) => `${label}`).join(' / ')
            : null;

        this.init(name, area, periodLabel, typeName);
    }


    preventMultipleCalls(){
        if(!!(this : any).refreshing){
            throw "prevent multiple calls in populateLayers";
            return;
        }
        (this : any).refreshing = true;
    }

    enablePopulateLayers(){
        (this : any).refreshing = false;
    }

    handleChangePeriod = el => {
        el.preventDefault();
        if(!this.refreshing){
            (this: any).updateDataSources();
            const layerPeriod = this._layerPeriods.findOrFirst(x => !!x.mainLayer);
            const datePeriods = {};
            ['start', 'end'].forEach(p => {
                let value = (this: any).period[p].value();
                datePeriods[p] = !!value ? value : layerPeriod.range[p];
            });

            this.refresh(datePeriods);
        }
    };

    _setPeriod(contexts, dates = null){
        const getDates = ctx => LayerControls(ctx).currentDatePeriod || ({start: new Date(), end: new Date()});
        const getPeriodLimits = ctx => LayerControls(ctx).periodLimits || {
            max: new Date(),
            min: moment(environment.get('CONFIG.FILTERS.STARTYEAR')).startOf('year').toDate()
        };

        this._layerPeriods = contexts
            .map(({label, context, mainLayer}) => {
                const period : FeaturePeriod = { mainLayer, label, context };

                if(mainLayer) {
                    period.range = dates || getDates(context);
                    period.limits = getPeriodLimits(context);
                }

                return period;
            });
    }

    get filterPeriod() {
        if (this.isAfterSales) {
            return {};
        }

        const layerPeriod = this._layerPeriods.findOrFirst(x => !!x.mainLayer);
        if (!layerPeriod) return {};

        const { range, limits } = layerPeriod;
        const filterPeriod = { ...range, min: limits.start, max: limits.end };

        if ((this: any).period) {
            const startInput = (this: any).period.start.value();
            const endInput = (this: any).period.end.value();
            filterPeriod.start = startInput || filterPeriod.start;
            filterPeriod.end = endInput || filterPeriod.end;
        }

        return filterPeriod;
    }


    get featuresProperties () {
        return (this: any)._features.map(f => f.properties);
    }

    async refresh(periodDates){
        if (!_visible || !this.container) {
            return;
        }
        this._setPeriod(this._layerPeriods, periodDates);
        await (this: any).populateLayers(true);
        const content = this.getContent(this._name, true, this._typeName);
        this.container.content = content;
        setTimeout(this.render.bind(this), 0);
        this.container.refresh();
        return this.container;
    }

    async init (name: string, area: any, period?: string, typeName?:string) {
        if (_visible && this.container) {
            this.container.close();
        }

        if (!area) {
            return null;
        }

        _visible = true;

        this.setShape(area);
        await (this: any).populateLayers();
        this.content = this.getContent(name, period, typeName);
        this.container = this.createContainer();
        this.container.open();
        setTimeout(this.render.bind(this), 0);

        return this.container;
    }

    getContent(name, period, typeName) {
        const{rangePicking} = this;
        return $(templates('dialog-selection', {
            type: name,
            stores: (this: any).calculateTotals,
            download: _.isFunction((this: any).saveAsExcel),
            period,
            typeName,
            rangePicking
        }));
    }

    render() {
        if (!_visible) {
            this.content.show();
            _visible = true;
        }
        (this: any)._type = this.createDropdown();
        (this: any)._chart = this.createChart();
        (this: any)._table = this.createTable();

        if (!this.isAfterSales) {
            (this: any).period = {};
            ['start', 'end'].forEach(p => {
                (this: any).period[p] = this.createDatePicker(p);
            });

            this.createChangePeriod();
        }

        if (_.isFunction((this: any).saveAsExcel)) {
            $(this.content.find('button#save-as').kendoButton({
                click: (this: any).saveAsExcel
            }));
        }

        this.createChartActions();
        this.updateLegend();
    }


    setShape (area: any) {
        (this: any)._shape = area.layer.toGeoJSON();

        if (area.layerType === 'circle')
            (this: any)._shape = turf.buffer(area.layer.toGeoJSON(), area.layer.getRadius(), { units: 'meters' });
    }

    addToLayers (layer: any) {
        _.forEach(layer.getCurrentFeatures(), (f) => {
            try {
                if (turf.booleanWithin(f, (this: any)._shape)) {
                    (this: any)._features.push(f);
                }
            } catch (err) {
                console.log('ERROR: ', err);
            }
        });
    }

    createContainer () {
        return this.content.kendoWindow({
            width: 400,
            resizable: false,
            draggable: true,
            visible: false,
            title: '',
            appendTo: 'body',
            close: () => {
                _visible = false;
                this.content.remove();
                this.container.destroy();
            },
            position: {
                top: 90,
                left: 270
            }
        }).data('kendoWindow');
    }

    createDropdown () {
        return this.content.find('select[name="properties"]').kendoDropDownList({
            dataTextField: 'text',
            dataValueField: 'value',
            dataSource: (this: any).createDropdownOptions(),
            change: this.updateDataSources
        }).data('kendoDropDownList');
    }

    createDatePicker (period : 'start' | 'end') : kendo.ui.DatePicker {
        const {range, limits} = this._layerPeriods.find(({mainLayer}) => !!mainLayer);
        return this.content.find(`input[name="${period}-period"]`).kendoDatePicker({
            start: "year",
            depth: "year",
            format: "MMMM yyyy",
            value: !!range ? range[period] : new Date(),
            min: !!limits ? limits.min : new Date(),
            max: !!limits ? limits.max : new Date(),
            dateInput: true
        }).data('kendoDatePicker');
    }

    createChart (dataSource: any, series: any) {
        return this.content.find('.chart')
            .kendoChart(this.getChartConfig(dataSource, series))
            .data('kendoChart');
    }

    createChartActions () {
        const typesChart = [ 'pie', 'bar', 'column' ];

        typesChart.forEach(type =>
            this.content.find(`#selection-${type}-chart-button`)
                .on('click', () => {
                    this.chartType = type;
                    this._chart.setOptions(this.getChartConfig());
                }));
    }

    createChangePeriod() {
        if(!!this.handleChangePeriod)
            this.content.find("#choose-period")
                .on('click', this.handleChangePeriod);
    }

    getChartConfig (dataSource: any, series: any) {
        return {
            title: {
                text: ''
            },
            dataSource: dataSource || this.createDataSource(10, 16),
            seriesDefaults: {
                type: this.chartType,
                labels: {
                    position: 'center',
                    visible: true,
                    background: 'transparent',
                    format: '{0:n0}',
                    font: `bold 10px 'Roboto Condensed', sans-serif`,
                    color: '#FFF'
                }
            },
            seriesColors: this.seriesColors,
            theme: 'bootstrap',
            chartArea: {
                width: 380,
                height: 175,
                margin: {
                    top: 0,
                    bottom: 0,
                    left: 10,
                    right: 10
                }
            },
            legend: {
                visible: true,
                position: 'right',
                width: 180,
                labels: {
                    font: `10px 'Roboto Condensed', sans-serif`
                }
            },
            series: series || [{
                field: 'value',
                categoryField: 'text',
                labels: { visible: true, format: '{0:n0}' },
                tooltip: { visible: true, format: '{0:n0}' }
            }]
        };
    }

    createTable (dataSource: any, columns: any) {
        return this.content.find('.table').kendoGrid(this.getTableConfig(dataSource, columns)).data('kendoGrid');
    }

    getTableConfig (dataSource: any, columns: any) {
        return {
            dataSource: dataSource || this.createDataSource(),
            scrollable: true,
            sortable: true,
            filterable: true,
            height: 150,
            columns: columns || [
                {
                    field: 'text',
                    title: 'Nome'
                },
                {
                    field: 'value',
                    title: 'Total',
                    width: 75
                },
                {
                    field: 'percent',
                    title: '(%)',
                    template: (data) => numeral(data.percentage).format('0,0[.]00%'),
                    width: 75
                }
            ]
        };
    }

    createDataSource (limit: number = 9999, truncate: number = 0, property: string = (this: any)._type.value()) {
        const total = _((this: any).aggregateBy(property)).values().sum();
        const data = _((this: any).aggregateBy(property))
            .map((val, key) => {
                if (truncate) {
                    return {
                        text: _.truncate(key, { length: truncate, omission: '…' }),
                        value: val,
                        percentage: val / total,
                        dateOpening: new Date(val.DateOpening)
                    };
                }
                return { text: key, value: val, percentage: val / total };
            })
            .orderBy(v => v.text)
            .orderBy(v => v.value)
            .reverse()
            .take(limit)
            .value();
        return new kendo.data.DataSource({ data });
    }

    updateDataSources () {
        (this: any)._table.setDataSource(this.createDataSource());
        (this: any)._chart.setDataSource(this.createDataSource(10, 16));
        this.updateLegend();
    }

    updateLegend () {
        const listAggregates = _((this: any).aggregateBy((this: any)._type.value()));
        const sumTotal = listAggregates.map(v => v).sum();
        const counter = listAggregates.map(() => 1).sum();

        const legends: {total: number, show: boolean}[] = [
            {total: sumTotal / counter, show: this.shouldSumByAverage()},
            {total: sumTotal, show: this.shouldSumByTotal()},
            {total: counter, show: true}
        ];

        const totalLegend = legends.find(x => x.show).total;

        this.content.find('.left .value.total').html(numeral((this: any).calculateTotals).format('0,0', Math.floor));

        this.content.find('.right .value.total').html(numeral(totalLegend).format('0,0', Math.floor));
        this.content.find('.right .text.type').html(`Total ${(this: any)._type.text().replace(/Total /, '')}`);
    }

    shouldSumByTotal () {
        return false;
    }

    shouldSumByAverage () {
        return false;
    }
}

export default Dialog;



// WEBPACK FOOTER //
// ./src/scripts/core/selection/Dialog.js